import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { track, getSource } from "@/lib/track";
import { toast } from "sonner";
import { btnPrimary, inputCls, labelCls } from "@/components/kit";
import { CheckCircle2, Phone } from "lucide-react";

const EVENT_TYPES = ["Inauguration", "Soirée d'entreprise", "Séminaire", "Lancement de produit", "Noël d'entreprise", "Congrès ou salon", "Autre"];

/**
 * Formulaire court « Être rappelé » — nom, société, téléphone, type d'événement.
 * Complète le parcours brief complet pour les prospects qui préfèrent le téléphone.
 */
export default function QuickCallbackForm({ testid = "quick-callback", title = "Être rappelé pour mon événement" }) {
  const [form, setForm] = useState({ name: "", company: "", phone: "", service: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Nom et téléphone sont requis."); return; }
    setLoading(true);
    try {
      await api.post("/submissions", { type: "rappel", ...form, source: getSource() });
      track("form_submit");
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (sent) {
    return (
      <div className="rounded-2xl border border-lime bg-lime p-8 text-center" data-testid={`${testid}-success`}>
        <CheckCircle2 size={32} className="mx-auto text-ink" />
        <p className="mt-3 font-serif text-2xl text-ink">C'est noté.</p>
        <p className="mt-2 text-sm text-ink/70">Un chef de projet vous rappelle sous 24 h ouvrées.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-ink/10 bg-white p-8 md:p-10" data-testid={testid} noValidate={false}>
      <p className="flex items-center gap-2.5 font-serif text-2xl text-ink"><Phone size={20} className="text-lime-dim" /> {title}</p>
      <p className="mt-2 text-sm text-ink/55">Quatre champs, trente secondes. Un chef de projet vous rappelle.</p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor={`${testid}-name`} className={labelCls}>Nom <span className="text-lime-dim">*</span></label>
          <input id={`${testid}-name`} type="text" required className={inputCls} data-testid={`${testid}-name`}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jean Dupont" />
        </div>
        <div>
          <label htmlFor={`${testid}-company`} className={labelCls}>Société</label>
          <input id={`${testid}-company`} type="text" className={inputCls} data-testid={`${testid}-company`}
            value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Votre entreprise" />
        </div>
        <div>
          <label htmlFor={`${testid}-phone`} className={labelCls}>Téléphone <span className="text-lime-dim">*</span></label>
          <input id={`${testid}-phone`} type="tel" required className={inputCls} data-testid={`${testid}-phone`}
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="06 12 34 56 78" />
        </div>
        <div>
          <label htmlFor={`${testid}-service`} className={labelCls}>Type d'événement</label>
          <select id={`${testid}-service`} className={`${inputCls} appearance-none`} data-testid={`${testid}-service`}
            value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
            <option value="">Sélectionnez…</option>
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <button type="submit" disabled={loading} onClick={() => track("click_rappel")} className={`${btnPrimary} mt-6 w-full justify-center disabled:opacity-60`} data-testid={`${testid}-submit`}>
        {loading ? "Envoi…" : "Me rappeler"}
      </button>
      <p className="mt-3 text-center text-xs text-ink/45">Réponse sous 24 h ouvrées · sans engagement</p>
    </form>
  );
}
